import { Icon } from "@dialogueconsulting/sked-ui";
import { cn } from "../../../../lib/utils";
import {
  MenuBoxOptionBase,
  MenuBoxProps,
  MenuBox,
} from "../../../sked-editor/social-mention/menu-box";
import { TooltipWrapper } from "../../../TooltipWrapper";
import { useState } from "react";

export interface SkedMentionItem extends MenuBoxOptionBase {
  displayName: string;
  profileImg?: string;
  info?: string;
  username?: string;
  tooltip?: string;
}

export interface SkedMentionBoxProps<T extends SkedMentionItem>
  extends MenuBoxProps<T> {
  headingText?: string;
  infoTooltip?: string;
  displayConfig?: {
    showProfileImage?: boolean;
    showInfo?: boolean;
    showUsername?: boolean;
    showRemove?: boolean;
    showItemTooltip?: boolean;
  };

  highlightBehavior?: {
    matchingOn: "start" | "contains";
    searchText: string | null | undefined;
  };
  onRemoveItem?: (item: T) => Promise<void>;
  initBlockLoading?: boolean;
}

export const SkedMentionBox = <T extends SkedMentionItem>({
  headingText,
  infoTooltip,
  displayConfig,
  highlightBehavior,
  onRemoveItem,
  initBlockLoading,
  ...props
}: SkedMentionBoxProps<T>) => {
  const [blockLoading, setBlockLoading] = useState(initBlockLoading);
  return (
    <MenuBox
      {...props}
      renderContentLayout={({ loadingJsx, itemsJsx, className }) => {
        return (
          <div className={cn(className, "flex flex-col relative")}>
            {headingText && (
              <span className="flex items-center px-[12px] py-[6px] whitespace-nowrap">
                <span className="font-semibold ">{headingText}</span>
                {infoTooltip && (
                  <TooltipWrapper
                    tooltip={infoTooltip || " "}
                    className="z-[99000]"
                  >
                    <Icon
                      className="ml-[8px]"
                      name="info-circle"
                      color="#808080"
                      size={16}
                    />
                  </TooltipWrapper>
                )}
                <span className="ml-[10px]">{loadingJsx}</span>
              </span>
            )}

            {itemsJsx}
            {blockLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/40">
                <i className="fas fa-spinner fa-spin text-xl text-gray-500"></i>
              </div>
            )}
          </div>
        );
      }}
      renderItem={(option, { className }) => (
        <TooltipContainer
          enabled={!!option.tooltip && displayConfig?.showItemTooltip}
          tooltip={option.tooltip}
          className={cn(`${className} flex items-center w-full`, {
            "py-[3px]": displayConfig?.showInfo,
          })}
        >
          {displayConfig?.showProfileImage && (
            <img
              className={`inline-block mr-[8px] size-[20px] rounded-full relative 
                after:absolute after:inset-0 after:bg-gray-200 `}
              src={option.profileImg || undefined}
              alt={option.displayName}
            />
          )}
          <span className="flex flex-col flex-1 min-w-0">
            <span className="truncate">
              <HighlightedLabel
                label={option.displayName}
                searchText={highlightBehavior?.searchText}
                matchingOn={highlightBehavior?.matchingOn}
                enabled={!!highlightBehavior}
              />
            </span>
            <div className="flex gap-2">
              {option.info && displayConfig?.showInfo && (
                <span className="text-neutral-500 text-[10px] whitespace-nowrap">
                  {option.info}
                </span>
              )}
              {displayConfig?.showUsername && option.username && (
                <span className="text-neutral-500 text-[10px] pl-[4px] ml-auto  whitespace-nowrap truncate">
                  <HighlightedLabel
                    label={option.username}
                    searchText={highlightBehavior?.searchText}
                    matchingOn={highlightBehavior?.matchingOn}
                    enabled={!!highlightBehavior}
                  />
                </span>
              )}
            </div>
          </span>
          {displayConfig?.showRemove && (
            <span
              className="ml-auto mr-[5px] text-neutral-500 hover:text-red-500 px-1"
              onMouseDown={async (e) => {
                setBlockLoading(true);
                try {
                  e.stopPropagation();
                  e.preventDefault();
                  await onRemoveItem?.(option);
                  await new Promise((r) => setTimeout(r, 300));
                } finally {
                  setBlockLoading(false);
                }
              }}
            >
              <i className="fa fa-xmark " />
            </span>
          )}
        </TooltipContainer>
      )}
    />
  );
};

const HighlightedLabel = ({
  label,
  searchText,
  matchingOn,
  enabled = true,
  className,
}: {
  label: string;
  searchText: string | null | undefined;
  matchingOn?: "start" | "contains";
  enabled?: boolean;
  className?: string;
}) => {
  if (!enabled || !searchText) {
    return <span className={className}>{label}</span>;
  }
  const lowerSearchText = (searchText || "").toLowerCase();
  const parts = splitMatchedParts(matchingOn || "contains", label, searchText);
  return (
    <span className={className}>
      {parts.map((part, index) => (
        <span
          key={index}
          className={
            part.toLowerCase() === lowerSearchText ? "font-bold" : "font-normal"
          }
        >
          {part}
        </span>
      ))}
    </span>
  );
};

const TooltipContainer = ({
  children,
  tooltip,
  className,
  enabled = true,
}: {
  children: React.ReactNode;
  tooltip: string | undefined;
  className?: string;
  enabled?: boolean;
}) => {
  if (!enabled) {
    return <div className={className}>{children}</div>;
  }
  return (
    <TooltipWrapper className={className} tooltip={tooltip}>
      {children}
    </TooltipWrapper>
  );
};

function regexEscape(text: string) {
  // eslint-disable-next-line unicorn/prefer-string-raw, unicorn/prefer-string-replace-all
  return String(text).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** ai-generated */
function splitMatchedParts(
  matchingOn: string,
  label: string,
  searchText: string | null | undefined
) {
  if (!searchText) {
    return [label];
  }

  if (
    matchingOn === "start" &&
    label.toLowerCase().startsWith(searchText.toLowerCase())
  ) {
    return [label.slice(0, searchText.length), label.slice(searchText.length)];
  }
  if (
    matchingOn === "contains" && // Split label into parts, highlighting all matches of searchText
    searchText
  ) {
    const regex = new RegExp(regexEscape(searchText), "gi");
    let lastIndex = 0;
    const parts: string[] = [];
    let match;
    while ((match = regex.exec(label)) !== null) {
      if (match.index > lastIndex) {
        parts.push(label.slice(lastIndex, match.index));
      }
      parts.push(label.slice(match.index, regex.lastIndex));
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < label.length) {
      parts.push(label.slice(lastIndex));
    }
    return parts;
  }

  return [label];
}
